import * as Api from '@ViewModels';
import { useMutation } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';

interface Params {
	id: string;
	/**
	 * When provided, the compliance PDF is emailed to this explicit address. When omitted, the API routes
	 * the copy to its default recipient (the logged-in user, or the impersonated client when impersonating),
	 * matching how the email/social `sendCompliance` endpoints behave.
	 */
	sendWithComplianceEmail?: string;
}

/**
 * Re-sends a pending blog's compliance PDF, mirroring the email/social compliance re-send flow.
 * Backed by `POST blog/{id}/sendCompliance?sendWithComplianceEmail=...` (BlogController.SendCompliance).
 */
export function useBlogSendComplianceMutation({
	impersonationContext,
	onError,
	onSuccess,
}: {
	impersonationContext?: Api.IImpersonationContext;
	onError?: (err: Api.IOperationResultNoValue) => void;
	onSuccess?: () => void;
} = {}) {
	const userSession = useUserSession();
	return useMutation({
		mutationFn: async ({ id, sendWithComplianceEmail }: Params) => {
			await userSession.webServiceHelper.callAsync(
				Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					// Only attach the query param when an explicit recipient is given; an empty value would
					// otherwise override the API's default-recipient behavior.
					queryParams: sendWithComplianceEmail ? { sendWithComplianceEmail } : undefined,
					urlPath: `blog/${encodeURIComponent(id)}/sendCompliance`,
				}),
				'POST',
				null
			);
		},
		onError,
		onSuccess,
	});
}
